import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';

import { AppDialog } from '@/shared/components/ui/AppDialog';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppInput } from '@/shared/components/ui/AppInput';
import { AppSelect } from '@/shared/components/ui/AppSelect';
import { CampaignStatus } from '@/shared/types/enums';
import type { AdPlatform } from '@/shared/types/enums';
import type { CampaignCreative } from '@/shared/types/entities';

import { CTA_OPTIONS } from '../../types/campaigns.types';
import { PLATFORM_CREATIVE_CONSTRAINTS } from '../../logic/platform-constraints';
import { useUpdateCreative } from '../../api/campaigns-mutations';
import type { UpdateCreativePayload } from '../../api/campaigns-api';
import { createCreativeEditSchema, type CreativeEditFormValues } from './CreativeEditDialog.schema';
import styles from './CreativeEditDialog.module.scss';

export interface CreativeEditDialogProps {
  campaignId: string;
  platform: AdPlatform;
  /** Drives whether the edit reaches the ad platform or only Promote's copy. */
  campaignStatus: CampaignStatus;
  /** Creative being edited; `null` keeps the dialog closed */
  creative: CampaignCreative | null;
  onClose: () => void;
}

export function CreativeEditDialog({
  campaignId,
  platform,
  campaignStatus,
  creative,
  onClose,
}: CreativeEditDialogProps) {
  const constraints = PLATFORM_CREATIVE_CONSTRAINTS[platform];
  const schema = useMemo(() => createCreativeEditSchema(platform), [platform]);
  const updateMutation = useUpdateCreative();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<CreativeEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { headline: '', description: '', callToAction: '', destinationUrl: '' },
  });

  // Re-seed whenever a different creative is opened.
  useEffect(() => {
    if (!creative) return;
    reset({
      headline: creative.headline ?? '',
      description: creative.description ?? '',
      callToAction: creative.callToAction ?? '',
      destinationUrl: creative.destinationUrl ?? '',
    });
  }, [creative, reset]);

  const headlineLength = watch('headline').length;
  const descriptionLength = watch('description').length;

  const onSubmit = (values: CreativeEditFormValues) => {
    if (!creative) return;

    // The endpoint treats every field as an optional patch, so send only what changed.
    const payload: UpdateCreativePayload = {};
    if (dirtyFields.headline) payload.headline = values.headline;
    if (dirtyFields.description) payload.body = values.description;
    if (dirtyFields.callToAction) payload.callToAction = values.callToAction;
    if (dirtyFields.destinationUrl) payload.destinationUrl = values.destinationUrl;

    updateMutation.mutate({ campaignId, creativeId: creative.id, payload }, { onSuccess: onClose });
  };

  return (
    <AppDialog
      open={creative !== null}
      onOpenChange={(open) => {
        if (!open && !updateMutation.isPending) onClose();
      }}
      title="Edit creative"
      description={`Copy limits follow the ad platform: headline ${constraints.headlines.maxLength} characters, description ${constraints.descriptions.maxLength}.`}
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <p className={styles.reach} role="note">
          {campaignStatus === CampaignStatus.DRAFT
            ? 'Saved now — this copy goes to the ad platform when you launch the campaign.'
            : 'Saving sends this copy to the ad platform and updates the existing ad.'}
        </p>

        <div className={styles.field}>
          <AppInput
            label="Headline"
            error={errors.headline?.message}
            maxLength={constraints.headlines.maxLength}
            {...register('headline')}
          />
          <span className={styles.counter}>
            {headlineLength}/{constraints.headlines.maxLength}
          </span>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="creative-description">
            Description
          </label>
          <textarea
            id="creative-description"
            className={styles.textarea}
            rows={3}
            maxLength={constraints.descriptions.maxLength}
            {...register('description')}
          />
          {errors.description && (
            <span className={styles.error} role="alert">
              {errors.description.message}
            </span>
          )}
          <span className={styles.counter}>
            {descriptionLength}/{constraints.descriptions.maxLength}
          </span>
        </div>

        <Controller
          control={control}
          name="callToAction"
          render={({ field }) => (
            <AppSelect
              label="Call to action"
              placeholder="Select a call to action..."
              options={CTA_OPTIONS}
              value={field.value || undefined}
              onValueChange={field.onChange}
              error={errors.callToAction?.message}
            />
          )}
        />

        <AppInput
          label="Destination URL"
          placeholder="https://events.com/your-event"
          error={errors.destinationUrl?.message}
          {...register('destinationUrl')}
        />

        <div className={styles.actions}>
          <AppButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </AppButton>
          <AppButton type="submit" loading={updateMutation.isPending} disabled={!isDirty}>
            <Save size={16} />
            Save changes
          </AppButton>
        </div>
      </form>
    </AppDialog>
  );
}
